import styled from "styled-components"
import { Link } from "react-router-dom"

const TEXT = "#101828"
const MUTED = "#667085"
const LINE = "#e4e7ec"

export const Page = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100svh;
`

export const Main = styled.main`
  display: flex;
  flex-direction: column;
  gap: 28px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 0 64px;

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    gap: 22px;
    padding: 12px 0 48px;
  }
`

export const Hero = styled.h1`
  margin: 0;
  text-align: center;
  font-size: clamp(30px, 6vw, 46px);
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: ${TEXT};
`

export const SellerBox = styled.section`
  padding: 20px 22px;
  border: 1px solid #dbe4ff;
  border-radius: 18px;
  background: linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
  box-shadow: 0 10px 28px rgba(47, 107, 255, 0.08);
`

export const SellerLabel = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.blue.main};
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

export const SellerName = styled.p`
  margin: 0;
  color: #252a35;
  font-size: 16px;
  line-height: 1.55;
`

export const SellerContact = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: ${({ theme }) => theme.colors.blue.main};
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    text-decoration: underline;
  }
`

export const Recovery = styled.section`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 20px;
  padding: 22px 24px;
  border: 1px solid #cbd9ff;
  border-radius: 20px;
  background: radial-gradient(
      circle at 0% 0%,
      rgba(49, 105, 255, 0.11),
      transparent 42%
    ),
    linear-gradient(180deg, #f8faff 0%, #ffffff 100%);
  box-shadow: 0 14px 34px rgba(47, 107, 255, 0.1);

  @media (max-width: 760px) {
    grid-template-columns: auto 1fr;
    gap: 15px;
    padding: 20px;
  }
`

export const RecoveryIcon = styled.div`
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  border-radius: 16px;
  color: #fff;
  background: ${({ theme }) => theme.colors.blue.main};
  box-shadow: 0 10px 24px rgba(47, 107, 255, 0.24);

  svg {
    width: 28px;
    height: 28px;
  }

  @media (max-width: 760px) {
    width: 48px;
    height: 48px;
    border-radius: 14px;
  }
`

export const RecoveryContent = styled.div`
  min-width: 0;

  h2 {
    margin: 0 0 7px;
    color: #242936;
    font-size: 20px;
    line-height: 1.25;
    letter-spacing: -0.02em;
  }

  p {
    margin: 0;
    color: #606777;
    font-size: 14px;
    line-height: 1.55;
  }

  @media (max-width: 760px) {
    h2 {
      font-size: 18px;
    }
  }
`

export const Kicker = styled.div`
  margin-bottom: 5px;
  color: ${({ theme }) => theme.colors.blue.main};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.09em;
  text-transform: uppercase;
`

export const RecoveryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 19px;
  border-radius: 13px;
  background: ${({ theme }) => theme.colors.blue.main};
  color: #fff;
  font-size: 14px;
  font-weight: 800;
  white-space: nowrap;
  text-decoration: none;
  box-shadow: 0 10px 22px rgba(47, 107, 255, 0.2);
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 13px 27px rgba(47, 107, 255, 0.26);
  }

  @media (max-width: 760px) {
    grid-column: 1 / -1;
    width: 100%;
  }
`

export const SectionTitle = styled.h2`
  margin: 0 0 14px;
  font-size: 28px;
  letter-spacing: -0.03em;
  color: ${TEXT};

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    font-size: 24px;
  }
`

export const FaqList = styled.div`
  border: 1px solid ${LINE};
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 14px 34px rgba(16, 24, 40, 0.07);
`

export const FaqItem = styled.div`
  & + & {
    border-top: 1px solid ${LINE};
  }
`

export const FaqQuestion = styled.button<{ $open: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 24px;
  border: 0;
  background: transparent;
  color: ${TEXT};
  font: inherit;
  font-size: 15px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;

  .sign {
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colors.blue.main};
    font-size: 24px;
    font-weight: 400;
    line-height: 1;
    transition: transform 0.2s ease;
    transform: rotate(${({ $open }) => ($open ? "45deg" : "0deg")});
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue.main};
    outline-offset: -2px;
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    padding: 18px;
  }
`

export const FaqAnswer = styled.div`
  padding: 0 24px 22px;
  color: ${MUTED};
  font-size: 14px;
  line-height: 1.65;

  p {
    margin: 0;
  }

  @media (max-width: ${({ theme }) => theme.bp.small}px) {
    padding: 0 18px 20px;
  }
`

export const FaqAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  margin-top: 14px;
  padding: 0 16px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.blue.main};
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
`

export const ImportantAlert = styled.aside`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 13px 16px;
  border: 1px solid #f3cf72;
  border-left: 4px solid #f2b705;
  border-radius: 12px;
  background: linear-gradient(180deg, #fffaf0 0%, #fffdf8 100%);
  box-shadow: 0 6px 16px rgba(160, 110, 0, 0.06);
  color: #4a4333;
  font-size: 13px;
  line-height: 1.5;

  .icon {
    flex: 0 0 auto;
    font-size: 18px;
    line-height: 1.2;
  }

  strong {
    color: #2e2a20;
    font-weight: 800;
  }
`
